import { ChevronDown, Loader2, Plus, Search, X } from 'lucide-react';
import * as React from 'react';
import { CheckSlot } from './check-row';
import { FormulaHint } from './formula-hint';
import { popoverScrollFix } from './lib/popover-scroll-fix';
import { summarizeSelection } from './lib/summarize-selection';
import { cn } from './lib/utils';
import { Badge } from './primitives/badge';
import { Popover, PopoverAnchor, PopoverContent } from './primitives/popover';

/**
 * The shared "choose from a list" control.
 *
 * ONE widget behind every picker in the product: the entity filters
 * (`EntityPicker`), the fixed-option filters, and the report builder's subject
 * area / columns / group-by / sort panels. They had drifted into three
 * near-identical implementations — a checkbox popover, an async combobox, and a
 * bespoke inline panel in `report-kit` — which is how the builder ended up with
 * a panel that PUSHED THE PAGE DOWN while every other dropdown in the app
 * floated over it.
 *
 * Two things make that impossible to regress:
 *
 *  - The panel is a Radix popover, so its content is portaled to
 *    `document.body`. It overlays. A picker inside a scrolling column can no
 *    longer be clipped by the scrollport, which was the original reason the
 *    builder rendered its panel inline in the first place.
 *  - The control is DATA-AGNOSTIC. It takes a plain option list and knows
 *    nothing about fetching, so the async layer is a wrapper (`EntityPicker`)
 *    rather than a fork. A caller with a static list — the report model's
 *    vocabulary — uses it directly.
 *
 * Single and multi are one component, chosen by `multiple`, because they differ
 * only in what a row click does and what the trigger summarizes. Two components
 * meant two sets of keyboard handling, two dismiss behaviours and two ideas of
 * what an empty list looks like.
 */

/**
 * What Radix asks of an anchor: a box, not an element.
 *
 * Declared here rather than imported from `@radix-ui/rect`, which is a
 * transitive package this one does not depend on directly.
 */
interface Measurable {
  getBoundingClientRect(): DOMRect;
}

export interface PickerOption {
  id: string;
  label: string;
  /** One sentence on what this is. Shown under the label. */
  description?: string;
  /**
   * A measure's formula. Not printed on the row — it sits behind an ƒ icon
   * beside the label (see `FormulaHint`). Named for what it is: a generic
   * "secondary line" field rendered as a formula would mislabel anything else
   * put in it.
   */
  formula?: string;
  /**
   * Present when the option cannot be picked; shown on the row INSTEAD of
   * letting it be chosen. A refusal that can lift ("already on Columns") is
   * kept visible with its reason rather than filtered out, because a row that
   * vanished as the draft changed reads as a bug.
   */
  disabledReason?: string;
  /** Section header. Contiguous runs sharing a group get one header. */
  group?: string;
}

export interface PickerOptionContext {
  isSelected: boolean;
  isHighlighted: boolean;
  query: string;
}

/**
 * What the bulk row above the list offers.
 *
 * `'select-all'` is opt-in rather than the default because it is only honest
 * when the list IS the whole vocabulary. In server-search mode the panel holds
 * one page, so a "Select all" there would tick what happens to have loaded and
 * silently ignore the rest.
 */
export type PickerBulkActions = 'select-all' | 'clear' | 'none';

interface PickerBaseProps {
  options: readonly PickerOption[];

  /** Accessible name for the panel, and the trigger's title. */
  title: string;
  /** Trigger text when nothing is chosen (`'value'`), or the verb (`'add'`). */
  placeholder?: string;

  /**
   * `'value'` (default) — a full-width trigger showing the current selection:
   * a label in single mode, removable chips in multi.
   * `'add'` — a compact "+ Add" button. For shelves that already list what is
   * chosen beneath them, where a trigger repeating the selection is furniture.
   */
  trigger?: 'value' | 'add';

  searchable?: boolean;
  searchPlaceholder?: string;
  /**
   * Open the panel on mount, once.
   *
   * For a control that is itself the answer to a click that just happened —
   * adding a filter to the bar, where choosing what to filter TO is the point
   * and a second click to open the values is a step between the intent and the
   * result. UNCONTROLLED: it seeds the initial state and nothing more, so the
   * panel dismisses normally and does not spring back open on a re-render.
   *
   * `onOpenChange` fires for it, on mount. A consumer's data layer learns it
   * should fetch from that callback, so a panel that opened without saying so
   * would sit on "Loading…" until the user closed and reopened it.
   */
  defaultOpen?: boolean;
  /**
   * Controlled query. Supplying it puts the picker in SERVER mode: it stops
   * filtering `options` in memory and renders whatever it is handed. Omit for
   * a static list and the panel filters itself over label, description, detail
   * and group.
   */
  query?: string;
  onQueryChange?: (query: string) => void;
  onOpenChange?: (open: boolean) => void;
  /**
   * Nothing has been fetched yet. With no `options` the body reads
   * `loadingText`; either way the trigger's caret turns into a spinner.
   *
   * The body keys on `options`, never on the search-filtered rows: in static
   * mode those differ, and a query that matched nothing over a loaded list is
   * "Nothing matches", not "Loading…".
   */
  loading?: boolean;
  /**
   * A fetch is in flight over rows ALREADY on screen. Spins the caret and marks
   * the list `aria-busy`, but inserts nothing into the panel, so the rows
   * neither shift nor stop being clickable.
   *
   * For server-mode callers, which is why it is separate from `loading`. They
   * hand over `options` already filtered, so from in here an empty list could
   * be "nothing fetched yet" or "the search matched nothing" — only the caller
   * knows which. A caller holding a fetched page says `refreshing`, and an
   * empty result then reads "Nothing matches" rather than claiming the catalog
   * has not arrived.
   *
   * The compact `'add'` trigger has no caret, so there both flags only set
   * `aria-busy`.
   */
  refreshing?: boolean;

  /** Row body. The checkmark gutter is drawn by this component. */
  renderOption?: (option: PickerOption, ctx: PickerOptionContext) => React.ReactNode;
  /**
   * Rendered at the top of the panel, above the search box — a control that
   * changes what a pick MEANS rather than what is picked, such as a report
   * filter's is / is not switch. The panel's list keys (arrows, Enter) are not
   * taken from it: Enter on a button here presses the button.
   */
  header?: React.ReactNode;
  /** Rendered under the list — a truncation notice, a "create new" CTA. */
  footer?: React.ReactNode;

  /** Shown when there are no options at all. */
  emptyText?: string;
  /** Shown when the query matches nothing. */
  noMatchText?: string;
  /** Wording for the body while nothing has been fetched. Override where
   *  "Loading…" is wrong for the domain (e.g. "Searching customers…"). */
  loadingText?: string;

  /**
   * `'anchor'` (default) matches the trigger width; `'auto'` uses it as a min
   * and grows to fit the widest row. Pick `'auto'` when rows carry detail the
   * trigger is too narrow for.
   */
  popoverWidth?: 'anchor' | 'auto';
  /**
   * Anchor the panel to THIS element rather than to the trigger.
   *
   * For a trigger that is one half of something the reader reads as a single
   * control: a report filter chip is the dimension's NAME beside this picker,
   * and anchored to the picker the panel opened indented by the width of that
   * name — far enough in that it read as a panel belonging to nothing on the
   * bar. Anchored to the chip it lines up with the thing that was clicked.
   *
   * The element is measured, never rendered into, so it may be any ancestor
   * (or none: an unset ref falls back to the trigger, so a chip that has not
   * mounted yet is not a broken anchor). It also becomes what
   * `popoverWidth: 'anchor'` matches.
   */
  anchorRef?: React.RefObject<HTMLElement | null>;
  disabled?: boolean;
  className?: string;
  /** Extra classes for the popover panel. */
  contentClassName?: string;
  id?: string;
}

interface PickerSingleProps extends PickerBaseProps {
  multiple?: false;
  value: string | null;
  onChange: (id: string | null, option: PickerOption | null) => void;
  /** Show the inline clear (×) on the trigger. Defaults true. */
  clearable?: boolean;
}

interface PickerMultiProps extends PickerBaseProps {
  multiple: true;
  value: readonly string[];
  onChange: (ids: string[]) => void;
  /** Defaults to `'clear'`. See {@link PickerBulkActions}. */
  bulkActions?: PickerBulkActions;
  /**
   * `'wrap'` (default) lets the trigger's chips wrap onto as many rows as they
   * need; `'scroll'` caps and scrolls them, so a filter bar's height stays put.
   */
  badgeOverflow?: 'wrap' | 'scroll';
  /** Float chosen rows to the top of the list. Defaults true in multi mode. */
  pinSelected?: boolean;
}

export type PickerProps = PickerSingleProps | PickerMultiProps;

export function Picker(props: PickerProps) {
  const {
    options,
    title,
    trigger = 'value',
    searchable = true,
    searchPlaceholder = 'Search…',
    defaultOpen = false,
    onQueryChange,
    onOpenChange,
    loading,
    refreshing,
    renderOption,
    header,
    footer,
    emptyText = 'Nothing to choose from',
    noMatchText,
    loadingText = 'Loading…',
    popoverWidth = 'anchor',
    anchorRef,
    disabled,
    className,
    contentClassName,
    id,
  } = props;

  const multiple = props.multiple === true;
  const serverMode = props.query !== undefined;
  // The two triggers want different defaults: the compact one is a VERB and
  // reads "Add", the wide one stands in for a value and reads "Select…".
  const placeholder = props.placeholder ?? (trigger === 'add' ? 'Add' : 'Select…');

  const [open, setOpen] = React.useState(defaultOpen);
  const [innerQuery, setInnerQuery] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  // The element focus returns to on close — the compact button or the value
  // trigger, whichever this picker renders.
  const triggerRef = React.useRef<HTMLElement>(null);
  // True when the panel is closing because the user interacted OUTSIDE it. A
  // pointer press elsewhere has already chosen where focus belongs, so taking
  // it back would be stealing; Escape and picking a row have not, so there the
  // trigger is the right place to land.
  const dismissedOutsideRef = React.useRef(false);
  const listboxId = React.useId();
  const optionDomId = (index: number) => `${listboxId}-option-${index}`;

  const query = serverMode ? (props.query ?? '') : innerQuery;

  const selectedIds = React.useMemo(
    () => (multiple ? [...props.value] : props.value ? [props.value] : []),
    [multiple, props.value],
  );
  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  const byId = React.useMemo(() => {
    const map = new Map<string, PickerOption>();
    for (const option of options) map.set(option.id, option);
    return map;
  }, [options]);

  // In server mode the caller has already filtered; in static mode the panel
  // matches over what it PRINTS — label, description, group — so typing
  // "hours" finds a measure by its description or its group as readily as by
  // its name. Not the formula: that sits behind the ƒ icon, and a row matched
  // on text nobody can see reads as a broken search.
  const matches = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (serverMode || !needle) return options;
    return options.filter((option) =>
      `${option.label} ${option.description ?? ''} ${option.group ?? ''}`
        .toLowerCase()
        .includes(needle),
    );
  }, [options, query, serverMode]);

  // Chosen rows first, so a long list never hides what is already on the
  // report. Only in multi mode: a single-select panel has one marked row, and
  // moving it would shuffle the list out from under the reader on every pick.
  const pinSelected = multiple ? ((props as PickerMultiProps).pinSelected ?? true) : false;
  const rows = React.useMemo(() => {
    if (!pinSelected) return matches;
    const chosen = matches.filter((o) => selectedSet.has(o.id));
    if (chosen.length === 0) return matches;
    return [...chosen, ...matches.filter((o) => !selectedSet.has(o.id))];
  }, [matches, pinSelected, selectedSet]);

  const setQuery = (next: string) => {
    setHighlightedIndex(-1);
    if (serverMode) onQueryChange?.(next);
    else {
      setInnerQuery(next);
      onQueryChange?.(next);
    }
  };

  // The mount-open announcement, fired ONCE. `handleOpenChange` covers every
  // other transition; a panel that started open never went through it, so
  // without this a `defaultOpen` picker never told its host to fetch.
  //
  // Mount only: the ref is what makes it fire once, and re-running when
  // `onOpenChange`'s identity changes would announce an open the user may
  // already have dismissed.
  const announceMountOpen = React.useRef(defaultOpen);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount only, by design
  React.useEffect(() => {
    if (!announceMountOpen.current) return;
    announceMountOpen.current = false;
    onOpenChange?.(true);
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (next) dismissedOutsideRef.current = false;
    setOpen(next);
    setHighlightedIndex(-1);
    onOpenChange?.(next);
    if (!next) setQuery('');
  };

  const commitSingle = (option: PickerOption | null) => {
    (props as PickerSingleProps).onChange(option?.id ?? null, option);
  };
  const commitMulti = (ids: string[]) => {
    (props as PickerMultiProps).onChange(ids);
  };

  /**
   * The new multi value: the caller's option order, with ids the panel cannot
   * see carried through untouched.
   *
   * `options` is NOT always the whole vocabulary. A server-mode panel holds one
   * page of a catalog, and a caller can legitimately hold a value whose row is
   * not currently offered — report-kit's sort chain keeps its `label` entry
   * after the last sortable dimension leaves the axis, so `label` stays in the
   * value while dropping out of `sortOptions()`. Rebuilding the value from
   * `options` alone silently discarded exactly those on the next tick, which
   * read as a sort entry deleting itself when an unrelated row was ticked.
   *
   * Unseen ids keep their existing relative order and are appended rather than
   * interleaved: the panel is not showing them, so it has no opinion on where
   * among the visible rows they belong.
   */
  const orderedValue = (chosen: ReadonlySet<string>): string[] => {
    const visible = options.map((o) => o.id).filter((oid) => chosen.has(oid));
    const seen = new Set(visible);
    return [...visible, ...selectedIds.filter((oid) => chosen.has(oid) && !seen.has(oid))];
  };

  const toggle = (optionId: string) => {
    const option = byId.get(optionId);
    // A refusal blocks ADDING, never removing. Guarding both directions left an
    // option that became unavailable while it was already selected stuck in the
    // value: its row would not untick, Enter did nothing, and the trigger chip's
    // × silently no-opped, so Clear all was the only way out.
    if (option?.disabledReason && !selectedSet.has(optionId)) return;
    if (multiple) {
      // The panel STAYS OPEN and the query stays typed: ticking a column is
      // one of twenty decisions, not the end of the errand.
      const next = new Set(selectedSet);
      if (next.has(optionId)) next.delete(optionId);
      else next.add(optionId);
      commitMulti(orderedValue(next));
    } else {
      commitSingle(option ?? null);
      handleOpenChange(false);
    }
  };

  const clearAll = () => {
    if (multiple) commitMulti([]);
    else commitSingle(null);
  };

  const bulkActions: PickerBulkActions = multiple
    ? ((props as PickerMultiProps).bulkActions ?? 'clear')
    : 'none';
  // "All" means everything pickable AND currently visible. Scoped to the
  // search results deliberately: typing "hours" then pressing Select all is
  // how a family of columns gets added, and a button that quietly also added
  // the rows just filtered out would be worse than no button.
  //
  // Memoized AND short-circuited: these feed the bulk row only, so a closed
  // single-select picker was walking a 500-row catalog three times per render
  // of its parent to build values it then threw away.
  const { selectableIds, allSelected } = React.useMemo(() => {
    if (!multiple || bulkActions === 'none') return { selectableIds: [], allSelected: false };
    const ids = rows.filter((o) => !o.disabledReason).map((o) => o.id);
    return {
      selectableIds: ids,
      allSelected: ids.length > 0 && ids.every((oid) => selectedSet.has(oid)),
    };
  }, [multiple, bulkActions, rows, selectedSet]);

  const selectAll = () => {
    const next = new Set(selectedSet);
    for (const oid of selectableIds) next.add(oid);
    commitMulti(orderedValue(next));
  };

  const moveHighlight = (delta: number) => {
    if (rows.length === 0) return;
    // From nothing highlighted, ArrowDown lands on the first row and ArrowUp on
    // the last — so the two seeds differ.
    let next = highlightedIndex < 0 ? (delta > 0 ? -1 : 0) : highlightedIndex;
    // Step over refused rows rather than landing on them: arrowing onto a row
    // that Enter cannot take reads as the keyboard being broken.
    for (let attempt = 0; attempt < rows.length; attempt++) {
      next = (next + delta + rows.length) % rows.length;
      if (!rows[next]?.disabledReason) break;
    }
    setHighlightedIndex(next);
    document.getElementById(optionDomId(next))?.scrollIntoView({ block: 'nearest' });
  };

  const headerRef = React.useRef<HTMLDivElement>(null);
  const handleListKeyDown = (event: React.KeyboardEvent) => {
    // A key pressed on a header control is that control's. Taken here, Enter on
    // the header's button would toggle whichever row was highlighted instead.
    if (headerRef.current?.contains(event.target as Node)) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveHighlight(-1);
        break;
      case 'Enter': {
        const picked = rows[highlightedIndex];
        if (picked) {
          event.preventDefault();
          toggle(picked.id);
        }
        break;
      }
      // No `Escape` case: Radix's `onEscapeKeyDown` already closes the panel,
      // and handling it here too ran the whole close path twice — firing
      // `onOpenChange` and `onQueryChange` twice per keypress.
    }
  };

  const singleClearable = !multiple && (props as PickerSingleProps).clearable !== false;
  const badgeOverflow = multiple ? ((props as PickerMultiProps).badgeOverflow ?? 'wrap') : 'wrap';
  const singleSelected = !multiple && selectedIds[0] ? byId.get(selectedIds[0]) : undefined;

  /**
   * The value trigger's accessible name: the field AND what is currently in it.
   *
   * `aria-label` beats element contents when the name is computed, so naming
   * the trigger after the field alone left every one of these announcing its
   * placeholder — "All" — whether or not anything was selected, with no audible
   * difference between a filtered and an unfiltered control. Composing both
   * halves is what the builder's own picker used to do, and it keeps the field
   * name a screen reader needs while restoring the value it lost.
   */
  // `summarizeSelection` past two, so a filter holding forty customers does not
  // read out an 800-character name — and re-read it on every change. The chips
  // carry the detail for anyone who wants it.
  const triggerValueLabel = multiple
    ? summarizeSelection(
        selectedIds.length,
        // Resolved only when they will be read — `summarizeSelection` counts
        // past two rather than listing, so mapping forty ids there builds a
        // forty-element array on every render to throw it away.
        selectedIds.length <= 2 ? selectedIds.map((sid) => byId.get(sid)?.label ?? '…') : [],
        placeholder,
      )
    : (singleSelected?.label ?? placeholder);
  // A blank placeholder is a VISUAL choice — a filter chip that shows the
  // dimension's name and nothing else until it has a value — and it must not
  // leave the trigger announcing "Customer:" with nothing after the colon.
  const triggerName = `${title}: ${triggerValueLabel || 'nothing chosen'}`;

  const hasGroups = rows.some((o) => o.group);

  // `options`, not `rows`: in static mode `rows` is the search-filtered subset,
  // and keying on it turned a query that matched nothing over a loaded list
  // into "Loading…". In server mode the two are the same length, so this is
  // exactly the old test there.
  const loadingBody = !!loading && options.length === 0;
  // Any fetch in flight, first page or re-search. Shown on the caret, never in
  // the list, so a re-search over the rows on screen shifts nothing.
  const busy = !!loading || !!refreshing;

  return (
    <Popover open={open}>
      {/* A VIRTUAL anchor, even in the ordinary case where it is this control's
          own container. `PopoverAnchor` measures whatever ref it is handed and
          renders nothing, so one code path serves both "anchor to the trigger"
          and a host's `anchorRef` — no branch in the tree, and no second
          wrapper element around the trigger for the hosted case to explain. */}
      <PopoverAnchor virtualRef={(anchorRef ?? containerRef) as React.RefObject<Measurable>} />
      <div ref={containerRef} className={cn('relative', trigger === 'add' ? 'w-fit' : 'w-full')}>
        {trigger === 'add' ? (
          <button
            type="button"
            ref={triggerRef as React.RefObject<HTMLButtonElement>}
            id={id}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={open ? listboxId : undefined}
            // `open` keeps it live: an empty option list disables the
            // trigger, and evaluating that while its own panel is up left
            // the one control that could dismiss it inert (with
            // `aria-expanded="true"` on a disabled button, which assistive
            // tech cannot act on). Reachable in the builder by emptying the
            // Columns shelf while the Sort panel is open.
            disabled={disabled || (!open && options.length === 0)}
            // The accessible name is the visible verb; `title` carries which
            // shelf it fills. Deliberately NOT an `aria-label` of the title:
            // a compact "+ Add" whose screen-reader name is "Choose columns"
            // reads as a different control from the one on screen.
            title={options.length === 0 ? emptyText : title}
            onClick={() => handleOpenChange(!open)}
            className={cn(
              'inline-flex items-center gap-1 rounded-md border border-dashed border-input bg-background px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-solid hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40',
              className,
            )}
          >
            <Plus className="size-3" aria-hidden />
            {placeholder}
          </button>
        ) : (
          // A focusable div, not a <button>: the clear and per-chip remove
          // affordances have to be real nested <button>s, and a button inside
          // a button is invalid HTML.
          <div
            ref={triggerRef as React.RefObject<HTMLDivElement>}
            id={id}
            // Same slot as the app's other select-equivalent triggers (see
            // `filter-bar.tsx`'s `ActiveFilterChip` and the `globals.css`
            // outlined-control block): this is a shadcn-free widget, so
            // nothing themed it centrally until now — border/background/
            // hover/focus and the pill radius all come from that one rule
            // once this is tagged.
            data-slot="combobox-trigger"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={open ? listboxId : undefined}
            aria-disabled={disabled || undefined}
            aria-label={triggerName}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && handleOpenChange(!open)}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                handleOpenChange(!open);
              }
            }}
            className={cn(
              'flex min-h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-left text-sm shadow-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
              className,
            )}
          >
            {multiple ? (
              selectedIds.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : (
                <span
                  className={cn(
                    'flex flex-1 flex-wrap items-center gap-1',
                    badgeOverflow === 'scroll' && 'max-h-16 overflow-y-auto',
                  )}
                >
                  {selectedIds.map((sid) => (
                    <SelectionChip
                      key={sid}
                      label={byId.get(sid)?.label ?? '…'}
                      disabled={disabled}
                      onRemove={() => toggle(sid)}
                    />
                  ))}
                </span>
              )
            ) : singleSelected ? (
              <span className="min-w-0 truncate font-medium">{singleSelected.label}</span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}

            <span className="flex shrink-0 items-center gap-1 text-muted-foreground/60">
              {singleClearable && selectedIds.length > 0 ? (
                <button
                  type="button"
                  aria-label="Clear selection"
                  tabIndex={-1}
                  // The wrapper only greys itself out (`aria-disabled` plus
                  // opacity) — it never stops pointer events — so without this
                  // a disabled picker could still be emptied in one click.
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  className="rounded-sm p-0.5 transition-colors hover:text-foreground disabled:pointer-events-none"
                >
                  <X size={14} />
                </button>
              ) : null}
              {/* The in-flight signal is the caret itself: visible with the
                  panel open or shut, and it adds nothing to the list that
                  would push the rows down on every debounced re-search.
                  Full-strength colour on the spinner alone: the wrapper's /60
                  tint made its thin arc nearly invisible, and lifting the
                  wrapper instead would also darken the clear × beside it. */}
              {busy ? (
                <Loader2
                  size={16}
                  aria-hidden="true"
                  className="animate-spin text-muted-foreground"
                />
              ) : (
                <ChevronDown
                  size={16}
                  className={cn('transition-transform', open && 'rotate-180')}
                  aria-hidden
                />
              )}
            </span>
          </div>
        )}
      </div>

      <PopoverContent
        align="start"
        sideOffset={4}
        className={cn(
          'overflow-hidden p-0',
          // `w-auto` is required to beat shadcn's `w-72` base class: without
          // it twMerge keeps the fixed 18rem and the min/max constraints have
          // nothing to interpolate between.
          popoverWidth === 'auto' ? 'w-auto max-w-md' : 'w-72',
          contentClassName,
        )}
        style={
          popoverWidth === 'auto'
            ? { minWidth: 'var(--radix-popper-anchor-width)' }
            : trigger === 'value'
              ? { width: 'var(--radix-popper-anchor-width)' }
              : undefined
        }
        ref={contentRef}
        // Focusable so it can take the caret when there is no search box to hold
        // it. `handleListKeyDown` is bound HERE, so with focus left on <body> the
        // arrow keys, Enter and Escape all missed the panel entirely.
        tabIndex={-1}
        // `aria-activedescendant` has to sit on whatever HOLDS focus. With a
        // search box that is the input (below); without one it is this panel,
        // and leaving it only on the input meant arrowing through rows in that
        // mode moved the highlight while announcing nothing at all.
        aria-activedescendant={
          !searchable && highlightedIndex >= 0 ? optionDomId(highlightedIndex) : undefined
        }
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (inputRef.current) inputRef.current.focus();
          else contentRef.current?.focus();
        }}
        // Radix cannot restore focus on its own here — the popover is anchored
        // rather than triggered — so hand it back explicitly. Without this,
        // Escape dropped focus to <body> and the next Tab restarted from the top
        // of the document instead of continuing past the trigger.
        //
        // But ONLY when the user did not point somewhere else. Radix's own
        // non-modal content makes the same distinction, and skipping it meant
        // clicking from an open panel straight into another field closed the
        // panel and then yanked focus back to the trigger, so the next keystroke
        // missed the field the user had just clicked into.
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          // And ONLY when nothing else has already taken it. Picking a row can
          // put a new focusable control on the page — adding a report filter
          // opens that filter's own panel on the same click, which is the whole
          // point of adding one — and that panel focuses itself while this one
          // is still unmounting. Yanking focus back to a trigger that has done
          // its job left the caret on "+ Filter" with an open search box beside
          // it, so the first thing typed went nowhere.
          //
          // "Nothing else has it" includes the panel itself, which is on its
          // way out: `isConnected` is the test, NOT `contentRef.current`. The
          // ref may already have been detached by the time this runs — React
          // nulls host refs during the deletion commit, and the ordering
          // against Radix's own unmount handler is not ours to decide — and a
          // containment check against a null ref would answer "somebody else
          // has focus" for the panel's own search box, quietly dropping the
          // Escape restore this line exists to make.
          const claimed = document.activeElement;
          const loose =
            claimed == null ||
            claimed === document.body ||
            !claimed.isConnected ||
            contentRef.current?.contains(claimed) === true;
          if (!dismissedOutsideRef.current && loose) triggerRef.current?.focus();
          dismissedOutsideRef.current = false;
        }}
        onInteractOutside={(e) => {
          const target = e.target as Node | null;
          // The anchor AND the trigger, never one instead of the other. With
          // `anchorRef` the control the reader sees is the whole chip, and a
          // press on the half of it that is not this picker — the dimension's
          // name — is a press on the control, not outside it; dismissing there
          // would close the panel and (an unfilled filter being taken off the
          // bar) delete the chip. But an anchor is only MEASURED, so nothing
          // makes it an ancestor of the trigger: dropping the container check
          // would leave a picker anchored to a sibling closing on pointerdown
          // and reopening on the click, unclosable from its own trigger.
          const inside = (node: Node) =>
            containerRef.current?.contains(node) === true ||
            anchorRef?.current?.contains(node) === true;
          if (target && inside(target)) {
            e.preventDefault();
            return;
          }
          // Runs before the unmount that fires `onCloseAutoFocus`, which reads
          // this to decide whether the trigger should take focus back.
          dismissedOutsideRef.current = true;
          handleOpenChange(false);
        }}
        onEscapeKeyDown={() => handleOpenChange(false)}
        onKeyDown={handleListKeyDown}
      >
        {header ? (
          <div ref={headerRef} className="border-b px-2.5 py-2">
            {header}
          </div>
        ) : null}

        {searchable ? (
          <div className="flex items-center gap-2 border-b px-2.5">
            <Search className="size-4 shrink-0 text-muted-foreground/60" aria-hidden />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              aria-controls={listboxId}
              aria-activedescendant={
                highlightedIndex >= 0 ? optionDomId(highlightedIndex) : undefined
              }
              autoComplete="off"
              className="h-9 w-full min-w-0 border-0 bg-transparent p-0 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
        ) : null}

        {multiple && bulkActions !== 'none' ? (
          <div className="flex items-baseline gap-3 border-b px-2.5 py-1.5 text-xs">
            {bulkActions === 'select-all' ? (
              <button
                type="button"
                disabled={selectableIds.length === 0 || allSelected}
                onClick={selectAll}
                className="text-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
              >
                {query.trim() ? `Select these ${selectableIds.length}` : 'Select all'}
              </button>
            ) : null}
            <button
              type="button"
              disabled={selectedIds.length === 0}
              // Clears the WHOLE selection, not just the visible rows: a
              // "clear all" that left some behind would be a lie.
              onClick={clearAll}
              className="text-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
            >
              Clear all
            </button>
            <span className="ml-auto text-muted-foreground">{selectedIds.length} chosen</span>
          </div>
        ) : null}

        <div
          id={listboxId}
          role="listbox"
          aria-multiselectable={multiple || undefined}
          aria-label={title}
          // With no status row in the list, this is how assistive tech learns
          // that the rows it is reading are about to be replaced. Not while the
          // loading status shows: it renders INSIDE this element, and a busy
          // region may hold its announcement until busy clears — by which time
          // it has unmounted.
          aria-busy={(busy && !loadingBody) || undefined}
          className="max-h-72 space-y-0.5 overflow-auto p-1"
          {...popoverScrollFix}
        >
          {loadingBody ? (
            // Pulses so an open panel reads as working, not as a stalled word;
            // `motion-safe` keeps it still for reduced-motion users.
            <div
              role="status"
              className="px-2 py-6 text-center text-sm text-muted-foreground motion-safe:animate-pulse"
            >
              {loadingText}
            </div>
          ) : rows.length === 0 ? (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
              {/* Keyed on whether a SEARCH is active, not on how many options
                  arrived. In server mode the caller pre-filters, so a query
                  that matched nothing arrives as an empty `options` and the
                  old `options.length` test called it an empty catalog —
                  telling someone who mistyped a customer name that there was
                  "nothing to choose from". */}
              {query.trim() ? (noMatchText ?? `Nothing matches “${query}”.`) : emptyText}
            </div>
          ) : (
            renderRows({
              rows,
              hasGroups,
              selectedSet,
              highlightedIndex,
              query,
              renderOption,
              optionDomId,
              toggle,
              setHighlightedIndex,
            })
          )}
        </div>

        {footer ? <div className="border-t">{footer}</div> : null}
      </PopoverContent>
    </Popover>
  );
}

/** Removable chip shown in the multi-select trigger. Built on the design-system
 *  `Badge` so chip styling tracks the rest of the app rather than drifting from
 *  a snapshot of its class string; the remove affordance is a real `<button>`
 *  (legal because the trigger is a div) that stops propagation so it doesn't
 *  also toggle the popover. */
function SelectionChip({
  label,
  disabled,
  onRemove,
}: {
  label: string;
  disabled?: boolean;
  onRemove: () => void;
}) {
  return (
    <Badge variant="secondary" className="gap-1 font-normal">
      <span className="max-w-40 truncate">{label}</span>
      <button
        type="button"
        aria-label={`Remove ${label}`}
        tabIndex={-1}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="rounded-sm hover:text-foreground disabled:pointer-events-none"
      >
        <X size={12} />
      </button>
    </Badge>
  );
}

/**
 * Render the listbox body, emitting a group header before the first row of each
 * contiguous run that shares a `group`.
 *
 * Pulled out of the component so the header decision — which needs single-pass
 * state across rows — reads as a loop instead of a mutable closure inside
 * `.map()`. Headers are visual only; keyboard navigation hops row to row.
 */
function renderRows({
  rows,
  hasGroups,
  selectedSet,
  highlightedIndex,
  query,
  renderOption,
  optionDomId,
  toggle,
  setHighlightedIndex,
}: {
  rows: readonly PickerOption[];
  hasGroups: boolean;
  selectedSet: ReadonlySet<string>;
  highlightedIndex: number;
  query: string;
  renderOption?: (option: PickerOption, ctx: PickerOptionContext) => React.ReactNode;
  optionDomId: (index: number) => string;
  toggle: (id: string) => void;
  setHighlightedIndex: (i: number) => void;
}): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let previousGroup: string | undefined;

  for (let index = 0; index < rows.length; index++) {
    const option = rows[index] as PickerOption;
    const isSelected = selectedSet.has(option.id);
    const isHighlighted = index === highlightedIndex;
    const refused = !!option.disabledReason;

    if (hasGroups && option.group !== previousGroup && option.group) {
      nodes.push(
        <div
          // Keyed by INDEX, not by the group name. The same group can head more
          // than one run — the caller's order is its own (report-kit lists every
          // dimension then every measure, and a group name recurs across that
          // seam), and `pinSelected` splits any group it floats a row out of.
          // Keying by name alone then emits sibling nodes sharing a key, which
          // React documents as unsupported: children may be duplicated or
          // dropped.
          key={`group-${index}-${option.group}`}
          className="px-2 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
        >
          {option.group}
        </div>,
      );
    }
    previousGroup = option.group;

    nodes.push(
      <button
        key={option.id}
        type="button"
        id={optionDomId(index)}
        role="option"
        aria-selected={isSelected}
        // Named explicitly: the row's own text is a paragraph of definition,
        // and a screen reader reading a formula before the name is a worse way
        // to find "Billable Hours" than sighted scanning.
        aria-label={option.label}
        // The formula is behind an icon here (it cannot be a focusable
        // control inside this button), so a screen reader gets it as the
        // row's description instead.
        aria-description={option.formula ? `Formula: ${option.formula}` : undefined}
        // `aria-disabled`, not `disabled`: a disabled button swallows pointer
        // events for everything inside it in some browsers, which made a
        // refused measure's ƒ icon dead — and "why can't I pick this?" is
        // exactly when its formula is worth reading. `toggle` is the guard
        // (it refuses adding, still allows removing), and the keyboard already
        // steps over refused rows.
        aria-disabled={refused || undefined}
        tabIndex={refused ? -1 : undefined}
        title={option.disabledReason}
        onClick={() => toggle(option.id)}
        onMouseEnter={() => {
          if (!refused && highlightedIndex !== index) setHighlightedIndex(index);
        }}
        className={cn(
          'flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors',
          // Same pale `--row-hover` lime as the Combobox option list and
          // table rows (see `combobox.tsx`'s `ComboboxOption`), not the blue
          // Selection wash.
          isHighlighted &&
            !refused &&
            'bg-accent text-accent-foreground light:bg-row-hover light:text-foreground',
          refused && 'cursor-not-allowed opacity-55',
        )}
      >
        <span className="mt-0.5">
          <CheckSlot isSelected={isSelected} />
        </span>
        <span className="grid min-w-0 flex-1 gap-0.5">
          {renderOption ? (
            renderOption(option, { isSelected, isHighlighted, query })
          ) : (
            <>
              <span className="flex min-w-0 items-center gap-1">
                {/* `min-w-0` here too, not just on the row above: a flex
                    ITEM defaults to `min-width: auto`, which for `nowrap`
                    text (what `truncate` sets) equals its full, unwrapped
                    width — so without overriding it on the label itself,
                    the row never shrinks the text down to where
                    `overflow-hidden` has anything to clip, and a long label
                    (an application's full name, say) pushes past the
                    popover's own border instead of ellipsizing. */}
                <span className="min-w-0 truncate font-medium">{option.label}</span>
                {option.formula ? <FormulaHint formula={option.formula} /> : null}
              </span>
              {option.description ? (
                <span className="text-xs text-muted-foreground">{option.description}</span>
              ) : null}
              {option.disabledReason ? (
                <span className="text-xs italic text-muted-foreground">
                  {option.disabledReason}
                </span>
              ) : null}
            </>
          )}
        </span>
      </button>,
    );
  }

  return nodes;
}
